/**
 * Settings as a dialog (board h39z): which settings section the URL asks for.
 *
 * `?settings=<section>` on the project layout route opens the dialog on top of
 * whatever project page is showing. `menu` is "the list" the sidebar gear
 * opens — on a phone that is the list screen, on desktop it shows Projekt.
 */
export const SETTINGS_SECTIONS = [
  'menu',
  'profile',
  'notifications',
  'project',
  'members',
  'plugins',
  'organization',
] as const

export type SettingsSection = (typeof SETTINGS_SECTIONS)[number]
/** A section with content (everything but the list). */
export type SettingsPane = Exclude<SettingsSection, 'menu'>

/** The two groups of the list, in order. */
export const ACCOUNT_PANES = ['profile', 'notifications'] as const satisfies SettingsPane[]
export const PROJECT_PANES = [
  'project',
  'members',
  // Board wwfu: behind the organization's `mcpPlugins` flag (`canSeePlugins`).
  'plugins',
  'organization',
] as const satisfies SettingsPane[]

export function parseSettingsSection(value: unknown): SettingsSection | undefined {
  return typeof value === 'string' && (SETTINGS_SECTIONS as readonly string[]).includes(value)
    ? (value as SettingsSection)
    : undefined
}

/** How the OAuth callback came back to the Plugins pane (board wwfu). */
export type McpCallbackStatus = 'connected' | 'error'

/**
 * `validateSearch` of the project layout route. Absent or invalid → no dialog.
 * `mcp` / `mcpStatus` / `mcpError` are set by the plugin OAuth callback
 * (`/<projectId>?settings=plugins&mcp=<id>&mcpStatus=connected`); the Plugins
 * pane shows a toast and strips them.
 */
export function validateSettingsSearch(search: Record<string, unknown>): {
  settings?: SettingsSection
  mcp?: string
  mcpStatus?: McpCallbackStatus
  mcpError?: string
} {
  const text = (value: unknown) =>
    typeof value === 'string' && value.length > 0 && value.length <= 80 ? value : undefined
  const status = search.mcpStatus
  return {
    settings: parseSettingsSection(search.settings),
    mcp: text(search.mcp),
    mcpStatus: status === 'connected' || status === 'error' ? status : undefined,
    mcpError: text(search.mcpError),
  }
}

/**
 * What the dialog shows for the requested section: desktop has no list screen
 * (`menu` → Projekt), and a section the user may not see shows Projekt. The
 * URL is left as it is.
 */
export function sectionForUser(
  section: SettingsSection,
  {
    isMobile,
    canSeeOrganization,
    canSeePlugins = false,
  }: { isMobile: boolean; canSeeOrganization: boolean; canSeePlugins?: boolean }
): SettingsSection {
  if (section === 'menu') return isMobile ? 'menu' : 'project'
  if (section === 'organization' && !canSeeOrganization) return 'project'
  if (section === 'plugins' && !canSeePlugins) return 'project'
  return section
}

/** The section an old `/$projectId/settings/<splat>` URL meant; anything else → Projekt. */
export function legacySettingsSection(splat: string | undefined): SettingsPane {
  const first = (splat ?? '').split('/').find(Boolean)
  return first === 'members' || first === 'organization' ? first : 'project'
}
